/**
 * AutomatedInvoiceGenerationPublisherAdvertiserPage — invoice list + generate
 * action for the publisher/advertiser invoice pipeline. GREEN-only accent.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	filterByRole,
	filterByStatus,
	formatCents,
	formatPeriod,
	invoiceTotalCents,
	type InvoiceStatus,
	type PartyRole,
} from "./api";
import {
	useInvoiceGenerate,
	useInvoiceList,
	useInvoiceSetStatus,
} from "./hooks";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const SELECT =
	"rounded-[6px] border border-zinc-300 bg-white px-2 py-1.5 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const BTN =
	"rounded-[6px] border border-green-700 bg-green-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";

const STATUS_PILL: Record<InvoiceStatus, string> = {
	draft: "bg-zinc-100 text-zinc-700 border-zinc-300",
	sent: "bg-green-50 text-green-800 border-green-300",
	paid: "bg-green-100 text-green-900 border-green-500",
	overdue: "bg-red-50 text-red-700 border-red-300",
};

const ROLES: Array<PartyRole | "all"> = ["all", "publisher", "advertiser"];
const STATUSES: Array<InvoiceStatus | "all"> = [
	"all",
	"draft",
	"sent",
	"paid",
	"overdue",
];

export function AutomatedInvoiceGenerationPublisherAdvertiserPage() {
	const listQuery = useInvoiceList();
	const genMut = useInvoiceGenerate();
	const statusMut = useInvoiceSetStatus();

	const [role, setRole] = useState<PartyRole>("publisher");
	const [partyId, setPartyId] = useState("");
	const [periodStart, setPeriodStart] = useState("");
	const [periodEnd, setPeriodEnd] = useState("");
	const [taxCents, setTaxCents] = useState("");

	const [filterRole, setFilterRole] = useState<PartyRole | "all">("all");
	const [filterStatus, setFilterStatus] = useState<InvoiceStatus | "all">(
		"all",
	);

	const allItems = listQuery.data?.items ?? [];

	const filtered = useMemo(() => {
		const byRole = filterByRole(allItems, filterRole);
		return filterByStatus(byRole, filterStatus);
	}, [allItems, filterRole, filterStatus]);

	const totalCents = useMemo(() => invoiceTotalCents(filtered), [filtered]);

	function toUnix(dateStr: string): number {
		if (!dateStr) return 0;
		const ms = Date.parse(`${dateStr}T00:00:00Z`);
		if (Number.isNaN(ms)) return 0;
		return Math.floor(ms / 1000);
	}

	const handleGenerate = (e: FormEvent) => {
		e.preventDefault();
		const start = toUnix(periodStart);
		const end = toUnix(periodEnd);
		if (!partyId.trim() || !start || !end || end <= start) return;
		const tax = taxCents.trim() === "" ? undefined : Number(taxCents);
		genMut.mutate(
			{
				role,
				party_id: partyId.trim(),
				period_start: start,
				period_end: end,
				...(tax !== undefined && !Number.isNaN(tax) ? { tax_cents: tax } : {}),
			},
			{
				onSuccess: () => {
					setPartyId("");
					setTaxCents("");
				},
			},
		);
	};

	return (
		<main
			data-testid="automated-invoice-generation-publisher-advertiser-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					automated-invoice-generation-publisher-advertiser
				</p>
				<h1 className="bg-gradient-to-r from-green-700 to-green-500 bg-clip-text text-3xl font-semibold text-transparent">
					Invoice Pipeline
				</h1>
				<p className="text-sm text-zinc-600">
					Automated invoices for publishers (payouts) and advertisers
					(conversions). Generate a draft per party per period, then move
					through sent, paid, or overdue.
				</p>
			</header>

			<section
				className={CARD}
				aria-label="Generate invoice"
				data-testid="automated-invoice-generation-publisher-advertiser-generate-section"
			>
				<h2 className="text-base font-semibold text-zinc-900">
					Generate invoice
				</h2>
				<p className="mt-1 text-xs text-zinc-500">
					Deterministic id per (role, party_id, period). Re-submitting the
					same tuple returns the existing draft.
				</p>
				<form
					onSubmit={handleGenerate}
					className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6"
					data-testid="automated-invoice-generation-publisher-advertiser-generate-form"
				>
					<label className="flex flex-col gap-1 text-xs text-zinc-600">
						Role
						<select
							className={SELECT}
							value={role}
							onChange={(e) => setRole(e.currentTarget.value as PartyRole)}
							disabled={genMut.isPending}
							data-testid="automated-invoice-generation-publisher-advertiser-role-input"
						>
							<option value="publisher">publisher</option>
							<option value="advertiser">advertiser</option>
						</select>
					</label>
					<label className="flex flex-col gap-1 text-xs text-zinc-600 sm:col-span-1 lg:col-span-2">
						Party id
						<input
							type="text"
							className={INPUT}
							placeholder="aff_123 or offer_456"
							value={partyId}
							onChange={(e) => setPartyId(e.currentTarget.value)}
							disabled={genMut.isPending}
							data-testid="automated-invoice-generation-publisher-advertiser-party-input"
						/>
					</label>
					<label className="flex flex-col gap-1 text-xs text-zinc-600">
						Period start
						<input
							type="date"
							className={INPUT}
							value={periodStart}
							onChange={(e) => setPeriodStart(e.currentTarget.value)}
							disabled={genMut.isPending}
							data-testid="automated-invoice-generation-publisher-advertiser-start-input"
						/>
					</label>
					<label className="flex flex-col gap-1 text-xs text-zinc-600">
						Period end
						<input
							type="date"
							className={INPUT}
							value={periodEnd}
							onChange={(e) => setPeriodEnd(e.currentTarget.value)}
							disabled={genMut.isPending}
							data-testid="automated-invoice-generation-publisher-advertiser-end-input"
						/>
					</label>
					<label className="flex flex-col gap-1 text-xs text-zinc-600">
						Tax (cents, optional)
						<input
							type="number"
							min={0}
							className={INPUT}
							placeholder="0"
							value={taxCents}
							onChange={(e) => setTaxCents(e.currentTarget.value)}
							disabled={genMut.isPending}
							data-testid="automated-invoice-generation-publisher-advertiser-tax-input"
						/>
					</label>
					<div className="flex items-end">
						<button
							type="submit"
							className={`${BTN} w-full`}
							disabled={genMut.isPending}
							data-testid="automated-invoice-generation-publisher-advertiser-submit"
						>
							{genMut.isPending ? "Generating." : "Generate now"}
						</button>
					</div>
				</form>
				{genMut.error ? (
					<p
						className="mt-2 text-xs text-red-600"
						role="alert"
						data-testid="automated-invoice-generation-publisher-advertiser-generate-error"
					>
						{genMut.error.message}
					</p>
				) : null}
			</section>

			<section
				className={CARD}
				aria-label="Invoices"
				data-testid="automated-invoice-generation-publisher-advertiser-list-section"
			>
				<div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
					<div>
						<h2 className="text-base font-semibold text-zinc-900">
							Invoices
						</h2>
						<p className="mt-1 text-xs text-zinc-500">
							Total (filtered):{" "}
							<span
								className="font-mono text-zinc-800"
								data-testid="automated-invoice-generation-publisher-advertiser-total"
							>
								{formatCents(totalCents)}
							</span>{" "}
							across{" "}
							<span
								data-testid="automated-invoice-generation-publisher-advertiser-count"
							>
								{filtered.length}
							</span>{" "}
							row(s).
						</p>
					</div>
					<div className="flex flex-wrap gap-3">
						<label className="flex flex-col gap-1 text-xs text-zinc-600">
							Role
							<select
								className={SELECT}
								value={filterRole}
								onChange={(e) =>
									setFilterRole(e.currentTarget.value as PartyRole | "all")
								}
								data-testid="automated-invoice-generation-publisher-advertiser-filter-role"
							>
								{ROLES.map((r) => (
									<option key={r} value={r}>
										{r}
									</option>
								))}
							</select>
						</label>
						<label className="flex flex-col gap-1 text-xs text-zinc-600">
							Status
							<select
								className={SELECT}
								value={filterStatus}
								onChange={(e) =>
									setFilterStatus(
										e.currentTarget.value as InvoiceStatus | "all",
									)
								}
								data-testid="automated-invoice-generation-publisher-advertiser-filter-status"
							>
								{STATUSES.map((s) => (
									<option key={s} value={s}>
										{s}
									</option>
								))}
							</select>
						</label>
					</div>
				</div>

				{listQuery.isLoading ? (
					<p
						className="mt-4 text-sm text-zinc-500"
						data-testid="automated-invoice-generation-publisher-advertiser-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p
						className="mt-4 text-sm text-red-600"
						role="alert"
						data-testid="automated-invoice-generation-publisher-advertiser-error"
					>
						{listQuery.error.message}
					</p>
				) : filtered.length === 0 ? (
					<p
						className="mt-4 text-sm text-zinc-500"
						data-testid="automated-invoice-generation-publisher-advertiser-empty"
					>
						No invoices match the current filter.
					</p>
				) : (
					<div className="mt-4 overflow-x-auto">
						<table
							className="min-w-full border-collapse text-left text-sm"
							data-testid="automated-invoice-generation-publisher-advertiser-table"
						>
							<thead className="border-b border-zinc-200 text-xs uppercase tracking-wider text-zinc-500">
								<tr>
									<th className="px-2 py-2 font-medium">Entity</th>
									<th className="px-2 py-2 font-medium">Period</th>
									<th className="px-2 py-2 text-right font-medium">
										Subtotal
									</th>
									<th className="px-2 py-2 text-right font-medium">Tax</th>
									<th className="px-2 py-2 text-right font-medium">Total</th>
									<th className="px-2 py-2 font-medium">Status</th>
									<th className="px-2 py-2 font-medium">PDF</th>
									<th className="px-2 py-2 font-medium">Actions</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-100">
								{filtered.map((row) => {
									const pdfHref = `/api/v1/automated-invoice-generation-publisher-advertiser/${encodeURIComponent(row.invoice_id)}.pdf`;
									return (
										<tr
											key={row.invoice_id}
											data-testid={`automated-invoice-generation-publisher-advertiser-row-${row.invoice_id}`}
										>
											<td className="px-2 py-2 align-top">
												<div className="text-xs uppercase tracking-wider text-zinc-500">
													{row.party_role}
												</div>
												<div className="font-mono text-xs text-zinc-800">
													{row.party_id}
												</div>
											</td>
											<td className="px-2 py-2 align-top text-xs text-zinc-700">
												<div>{formatPeriod(row.period_start)}</div>
												<div className="text-zinc-400">to</div>
												<div>{formatPeriod(row.period_end)}</div>
											</td>
											<td className="px-2 py-2 align-top text-right font-mono text-xs text-zinc-700">
												{formatCents(row.subtotal_cents, row.currency)}
											</td>
											<td className="px-2 py-2 align-top text-right font-mono text-xs text-zinc-700">
												{formatCents(row.tax_cents, row.currency)}
											</td>
											<td className="px-2 py-2 align-top text-right font-mono text-xs font-semibold text-zinc-900">
												{formatCents(row.total_cents, row.currency)}
											</td>
											<td className="px-2 py-2 align-top">
												<span
													className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${STATUS_PILL[row.status]}`}
													data-testid={`automated-invoice-generation-publisher-advertiser-status-${row.invoice_id}`}
												>
													{row.status}
												</span>
											</td>
											<td className="px-2 py-2 align-top">
												<a
													href={pdfHref}
													className="text-xs font-medium text-green-700 underline-offset-2 hover:underline"
													data-testid={`automated-invoice-generation-publisher-advertiser-pdf-${row.invoice_id}`}
												>
													pdf
												</a>
											</td>
											<td className="px-2 py-2 align-top">
												<div className="flex flex-wrap gap-1">
													{row.status === "draft" ? (
														<button
															type="button"
															className={BTN_GHOST}
															onClick={() =>
																statusMut.mutate({
																	invoiceId: row.invoice_id,
																	status: "sent",
																})
															}
															disabled={statusMut.isPending}
															data-testid={`automated-invoice-generation-publisher-advertiser-send-${row.invoice_id}`}
														>
															Send
														</button>
													) : null}
													{row.status === "sent" ? (
														<>
															<button
																type="button"
																className={BTN_GHOST}
																onClick={() =>
																	statusMut.mutate({
																		invoiceId: row.invoice_id,
																		status: "paid",
																	})
																}
																disabled={statusMut.isPending}
																data-testid={`automated-invoice-generation-publisher-advertiser-paid-${row.invoice_id}`}
															>
																Mark paid
															</button>
															<button
																type="button"
																className={BTN_GHOST}
																onClick={() =>
																	statusMut.mutate({
																		invoiceId: row.invoice_id,
																		status: "overdue",
																	})
																}
																disabled={statusMut.isPending}
																data-testid={`automated-invoice-generation-publisher-advertiser-overdue-${row.invoice_id}`}
															>
																Overdue
															</button>
														</>
													) : null}
													{row.status === "overdue" ? (
														<button
															type="button"
															className={BTN_GHOST}
															onClick={() =>
																statusMut.mutate({
																	invoiceId: row.invoice_id,
																	status: "paid",
																})
															}
															disabled={statusMut.isPending}
															data-testid={`automated-invoice-generation-publisher-advertiser-paid-${row.invoice_id}`}
														>
															Mark paid
														</button>
													) : null}
												</div>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</section>

			<section
				className={CARD}
				aria-label="Schedule"
				data-testid="automated-invoice-generation-publisher-advertiser-schedule-section"
			>
				<h2 className="text-base font-semibold text-zinc-900">
					Schedule settings
				</h2>
				<p className="mt-1 text-xs text-zinc-500">
					Invoices currently generate on-demand via the form above. Cron
					scheduling lands once the billing workflow opts in per tenant.
				</p>
				<dl className="mt-3 grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
					<div>
						<dt className="text-xs text-zinc-500">Mode</dt>
						<dd className="font-medium text-zinc-900">Manual</dd>
					</div>
					<div>
						<dt className="text-xs text-zinc-500">Cadence</dt>
						<dd className="font-medium text-zinc-900">Monthly (planned)</dd>
					</div>
					<div>
						<dt className="text-xs text-zinc-500">Next run</dt>
						<dd className="font-medium text-zinc-900">-</dd>
					</div>
				</dl>
			</section>
		</main>
	);
}

export default AutomatedInvoiceGenerationPublisherAdvertiserPage;
